import { eq, and, lt, gt } from 'drizzle-orm';
import { db } from '../../../db';
import { retryableQuery } from '../../../functions/retryableQuery';
import { dataDeletionRequests } from '../models/dataDeletionRequests';
import { DataDeletionRequest } from '../types/DataDeletionRequest';
import { DELETION_REQUEST_STATUS } from '../constants/DELETION_REQUEST_STATUS';
import type { DeletionRequestStatus } from '../types/DeletionRequestStatus';
import { BaseRepository } from '../../../classes/BaseRepository';
import { convertToAppError, AppError, AsyncResult, err, ok } from '@shared/utils';
import { HTTP_STATUS_NAMES, DATA_API_MESSAGES } from '@shared/constants';

/**
 * @group User
 * @summary Data deletion request repository
 * 
 * <blockquote>
 * Provides data access methods for data deletion request management, including
 * token creation, validation, and status updates. Extends BaseRepository for
 * common CRUD operations while adding domain-specific methods for the deletion
 * request lifecycle.
 * </blockquote>
 * 
 * @example
 * ```typescript
 * const repo = new DataDeletionRequestRepository();
 * const request = await repo.createRequest('user@example.com', token, expiresAt);
 * const validated = await repo.getByToken(token);
 * ```
 */
export class DataDeletionRequestRepository extends BaseRepository<DataDeletionRequest> {
  protected table = dataDeletionRequests;

  /**
   * Creates a new deletion request with the given email and token
   * 
   * @param email - User's email address
   * @param token - Secure verification token (128 hex characters)
   * @param expiresAt - Token expiration timestamp
   * @returns AsyncResult containing the created request or error
   */
  async createRequest(
    email: string,
    token: string,
    expiresAt: Date
  ): AsyncResult<DataDeletionRequest, AppError> {
    const normalizedEmail = email.toLowerCase().trim();
    
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.insert(this.table).values({
            email: normalizedEmail,
            token,
            status: DELETION_REQUEST_STATUS.PENDING,
            expires_at: expiresAt,
          }).returning()
        );

        return result.match({
          ok: (rows) => ok(rows[0]),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Retrieves a deletion request by its verification token
   * 
   * @param token - The verification token
   * @returns AsyncResult containing the request or null if not found
   */
  async getByToken(token: string): AsyncResult<DataDeletionRequest | null, AppError> {
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select()
            .from(this.table)
            .where(eq(this.table.token, token))
        );

        return result.match({
          ok: (rows) => ok(rows.length > 0 ? rows[0] : null),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Retrieves a valid (pending and not expired) deletion request by token
   * 
   * @param token - The verification token
   * @returns AsyncResult containing the valid request or null
   */
  async getValidRequest(token: string): AsyncResult<DataDeletionRequest | null, AppError> {
    const now = new Date();
    
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select()
            .from(this.table)
            .where(
              and(
                eq(this.table.token, token),
                eq(this.table.status, DELETION_REQUEST_STATUS.PENDING),
                gt(this.table.expires_at, now)
              )
            )
        );

        return result.match({
          ok: (rows) => ok(rows.length > 0 ? rows[0] : null),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Updates the status of a deletion request
   * 
   * @param id - Request internal ID
   * @param status - New status value
   * @param completedAt - Optional completion timestamp (for 'completed' status)
   * @returns AsyncResult containing the updated request or error
   */
  async updateStatus(
    id: string,
    status: DeletionRequestStatus,
    completedAt?: Date
  ): AsyncResult<DataDeletionRequest, AppError> {
    const updateData: Partial<DataDeletionRequest> = { status };
    
    if (completedAt && status === DELETION_REQUEST_STATUS.COMPLETED) {
      updateData.completed_at = completedAt;
    }

    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set(updateData)
            .where(eq(this.table.id, id))
            .returning()
        );

        return result.match({
          ok: (rows) => {
            if (rows.length === 0) {
              return err(convertToAppError(
                `Deletion request not found: ${id}`,
                DATA_API_MESSAGES.ERRORS.ID_REQUIRED,
                HTTP_STATUS_NAMES.NOT_FOUND
              ));
            }
            return ok(rows[0]);
          },
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Retrieves pending requests by email address
   * Used to check for existing pending requests before creating new ones
   * 
   * @param email - User's email address
   * @returns AsyncResult containing array of pending requests
   */
  async getPendingByEmail(email: string): AsyncResult<DataDeletionRequest[], AppError> {
    const normalizedEmail = email.toLowerCase().trim();
    
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.select()
            .from(this.table)
            .where(
              and(
                eq(this.table.email, normalizedEmail),
                eq(this.table.status, DELETION_REQUEST_STATUS.PENDING)
              )
            )
        );

        return result.match({
          ok: (rows) => ok(rows),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }

  /**
   * Marks expired pending requests as expired
   * Used by cleanup jobs to maintain data hygiene
   * 
   * @returns AsyncResult containing count of expired requests
   */
  async markExpiredRequests(): AsyncResult<number, AppError> {
    const now = new Date();
    
    return db.match({
      ok: async (database) => {
        const result = await retryableQuery(() =>
          database.update(this.table)
            .set({ status: DELETION_REQUEST_STATUS.EXPIRED })
            .where(
              and(
                eq(this.table.status, DELETION_REQUEST_STATUS.PENDING),
                lt(this.table.expires_at, now)
              )
            )
            .returning()
        );

        return result.match({
          ok: (rows) => ok(rows.length),
          err: (error) => err(error)
        });
      },
      err: async (error) => err(error)
    });
  }
}
